import { api } from "@/lib/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@a7y/tailwind-catalyst";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type { LearningTopicBroadcastResponse, LearningTopicDoc } from "@a7y/types";

interface BroadcastConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  topic: LearningTopicDoc;
  onComplete: () => void;
}

export function BroadcastConfirmDialog({
  open,
  onClose,
  topic,
  onComplete,
}: BroadcastConfirmDialogProps) {
  const [result, setResult] = useState<LearningTopicBroadcastResponse | null>(null);

  const broadcastMutation = useMutation({
    mutationFn: () => api.broadcastLearningTopic(topic.id),
    onSuccess: (data) => setResult(data),
    onError: () => {},
  });

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const handleConfirm = () => {
    broadcastMutation.mutate();
  };

  const handleDone = () => {
    setResult(null);
    onComplete();
  };

  return (
    <Dialog open={open} onClose={handleClose} size="sm">
      <DialogTitle>
        {result ? "Broadcast Complete" : "Confirm Broadcast"}
      </DialogTitle>
      <DialogBody>
        {result ? (
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-zinc-500 dark:text-zinc-400">
                Total Recipients
              </dt>
              <dd className="font-medium text-zinc-900 dark:text-white">
                {result.totalRecipients}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-zinc-500 dark:text-zinc-400">Sent</dt>
              <dd className="font-medium text-green-600 dark:text-green-400">
                {result.totalSent}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-zinc-500 dark:text-zinc-400">Skipped</dt>
              <dd className="font-medium text-zinc-900 dark:text-white">
                {result.totalSkipped}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-zinc-500 dark:text-zinc-400">Failed</dt>
              <dd className="font-medium text-red-600 dark:text-red-400">
                {result.totalFailed}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-zinc-500 dark:text-zinc-400">Deferred</dt>
              <dd className="font-medium text-zinc-900 dark:text-white">
                {result.totalDeferred}
              </dd>
            </div>
          </dl>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              Are you sure you want to broadcast this topic? This will send the
              educational email to all eligible users who have not yet adopted
              the feature.
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-zinc-500 dark:text-zinc-400">Topic</dt>
                <dd className="font-medium text-zinc-900 dark:text-white">
                  {topic.title}
                </dd>
              </div>
            </dl>
            {broadcastMutation.isError && (
              <div className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400">
                {broadcastMutation.error instanceof Error
                  ? broadcastMutation.error.message
                  : "Broadcast failed"}
              </div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        {result ? (
          <Button color="blue" onClick={handleDone}>
            Done
          </Button>
        ) : (
          <>
            <Button plain onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="red"
              disabled={broadcastMutation.isPending}
              onClick={handleConfirm}
            >
              {broadcastMutation.isPending ? "Broadcasting..." : "Broadcast Now"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
